import React, { useEffect, useRef } from 'react';

const FADE_MS = 500;
const FADE_OUT_LEAD = 0.55;

const FadingVideo = ({ src, className, style, poster }) => {
  const videoRef = useRef(null);
  const rafRef = useRef(null);
  const fadingOutRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const fadeTo = (target, duration = FADE_MS) => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      const start = performance.now();
      const from = parseFloat(video.style.opacity || '0');
      const tick = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const v = from + (target - from) * t;
        video.style.opacity = String(v);
        if (t < 1) rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const onLoaded = () => {
      video.style.opacity = '0';
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // autoplay blocked; show first frame statically
          video.style.opacity = '1';
        });
      }
      fadeTo(1);
    };
    const onTime = () => {
      if (!video.duration) return;
      if (
        video.duration - video.currentTime <= FADE_OUT_LEAD &&
        !fadingOutRef.current
      ) {
        fadingOutRef.current = true;
        fadeTo(0);
      }
    };
    const onEnded = () => {
      video.style.opacity = '0';
      setTimeout(() => {
        try {
          video.currentTime = 0;
          video.play().catch(() => {});
        } catch (e) {
          /* noop */
        }
        fadingOutRef.current = false;
        fadeTo(1);
      }, 100);
    };
    const onError = () => {
      // graceful fallback: hide broken video
      video.style.opacity = '0';
    };

    video.addEventListener('loadeddata', onLoaded);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('ended', onEnded);
    video.addEventListener('error', onError);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      video.removeEventListener('loadeddata', onLoaded);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('error', onError);
    };
  }, []);

  return (
    <video
      ref={videoRef}
      src={src}
      poster={poster}
      autoPlay
      muted
      playsInline
      preload="metadata"
      aria-hidden="true"
      tabIndex={-1}
      className={className}
      style={{ opacity: 0, ...style }}
    />
  );
};

export default FadingVideo;
